import React, { Component } from "react";
import { captureException } from "../utils/sentry";
import config from "../config";

const CONTACT_EMAIL = config.business.contactEmail;

/**
 * ErrorBoundary Component
 *
 * Catches React errors in child components and displays a fallback UI.
 * This prevents the entire application from crashing when an error occurs.
 *
 * Usage:
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    // Update state with error details
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Send error to Sentry for monitoring
    captureException(error, {
      component: 'ErrorBoundary',
      action: 'componentDidCatch',
      errorInfo: errorInfo
    });
  }

  handleReload = () => {
    // Reset error state and reload the page
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Fallback UI when error occurs
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '2rem',
          backgroundColor: '#f5f5f5',
          textAlign: 'center'
        }}>
          <div style={{
            maxWidth: '600px',
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
          }}>
            <h1 style={{
              color: '#d32f2f',
              marginBottom: '1rem',
              fontSize: '1.5rem'
            }}>
              Oops! Something went wrong
            </h1>

            <p style={{
              color: '#666',
              marginBottom: '1.5rem',
              lineHeight: '1.6'
            }}>
              Sorry for the inconvenience. An unexpected error has occurred.
              Please try reloading the page or contact support if the problem persists.
            </p>

            {/* Show error details in development mode */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details style={{
                marginBottom: '1.5rem',
                textAlign: 'left',
                backgroundColor: '#f5f5f5',
                padding: '1rem',
                borderRadius: '4px',
                fontSize: '0.875rem'
              }}>
                <summary style={{
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  marginBottom: '0.5rem',
                  color: '#d32f2f'
                }}>
                  Error Details (Development Mode)
                </summary>
                <pre style={{
                  overflow: 'auto',
                  fontSize: '0.75rem',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word'
                }}>
                  {this.state.error.toString()}
                  {this.state.errorInfo && this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}

            <button
              onClick={this.handleReload}
              style={{
                backgroundColor: '#7c8799',
                color: 'white',
                border: 'none',
                padding: '0.75rem 2rem',
                borderRadius: '4px',
                fontSize: '1rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#6a7385'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#7c8799'}
            >
              Reload Page
            </button>

            <p style={{
              marginTop: '1.5rem',
              fontSize: '0.875rem',
              color: '#999'
            }}>
              If this issue continues, please contact me at{' '}
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                style={{ color: '#7c8799', textDecoration: 'underline' }}
              >
                {CONTACT_EMAIL}
              </a>
            </p>
          </div>
        </div>
      );
    }

    // When there's no error, render children normally
    return this.props.children;
  }
}

export default ErrorBoundary;
